import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Terminal, ShieldCheck, Cloud, Eye, EyeOff } from "lucide-react";
import { useSettings } from "../../context/SettingsContext";

export default function AdminLogin() {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const { settings } = useSettings();
  const { login }    = useAdminAuth();
  const navigate     = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = await login(email, password);
      const perms = user.permissions || [];
      const isSuperAdmin = perms.includes("*");
      // Redirect to first page the user has permission to access
      if (isSuperAdmin || perms.includes("dashboard")) navigate("/admin");
      else if (perms.includes("orders"))    navigate("/admin/orders");
      else if (perms.includes("inventory")) navigate("/admin/products");
      else if (perms.includes("messages"))  navigate("/admin/messages");
      else if (perms.includes("invoices"))  navigate("/admin/invoices");
      else if (perms.includes("customers")) navigate("/admin/customers");
      else if (perms.includes("requests"))  navigate("/admin/quotes");
      else if (perms.includes("shipping"))  navigate("/admin/shipping");
      else navigate("/admin"); // fallback
    } catch (err) {
      const detail = err.response?.data?.detail || "Login failed";
      setError(typeof detail === "string" ? detail : "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "#eef2f8" }} data-testid="admin-login-page">

      {/* ── Left — branded dark panel ─────────────────────────────── */}
      <div
        className="hidden lg:flex flex-col justify-between w-[420px] shrink-0 p-12"
        style={{ background: "#0b1a3b" }}
      >
        {/* Logo */}
        <div>
          <img
            src={settings.logo_url || "/logo.png"}
            onError={(e) => { e.target.src = "/logo.png"; }}
            alt="BCP"
            className="h-16 w-auto object-contain mb-8 bg-white rounded-xl p-2"
          />
          <div className="font-heading text-[26px] font-bold text-white leading-tight mb-2">
            BCP Admin<br />Control Panel
          </div>
          <div className="text-[12px] font-mono uppercase tracking-[0.2em]" style={{ color: "#93c5fd" }}>
            Brisbane Car Parts · ERP
          </div>
        </div>

        {/* Feature list */}
        <div className="space-y-4">
          {[
            { icon: Cloud,       text: "Live Pinnacle sync engine" },
            { icon: ShieldCheck, text: "JWT-secured admin access" },
            { icon: Terminal,    text: "Offline-safe queue system" },
          ].map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: "rgba(227,30,36,0.15)", border: "1px solid rgba(227,30,36,0.3)" }}>
                <Icon className="h-4 w-4" style={{ color: "#e31e24" }} />
              </div>
              <span className="text-[13px]" style={{ color: "#93c5fd" }}>{text}</span>
            </div>
          ))}
        </div>

        <div className="text-[11px] font-mono" style={{ color: "#475569" }}>
          © {new Date().getFullYear()} Brisbane Car Parts
        </div>
      </div>

      {/* ── Right — login form ────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img src={settings.logo_url || "/logo.png"} onError={(e) => { e.target.src = "/logo.png"; }}
              alt="BCP" className="h-14 w-auto object-contain bg-white rounded-xl p-2 shadow-sm" />
          </div>

          <div
            className="bg-white rounded-2xl p-8 shadow-lg"
            style={{ border: "1px solid #dde3ef" }}
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1.5" style={{ color: "#94a3b8" }}>
              Admin Console
            </div>
            <h1 className="font-heading text-[28px] font-bold mb-6" style={{ color: "#0f172a" }}>
              Sign In
            </h1>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-login-form">
              <div>
                <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>
                  Email address
                </Label>
                <Input
                  type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 rounded-xl h-11"
                  style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }}
                  placeholder="admin@bcpcarparts.com.au"
                  required autoComplete="email" autoFocus
                  data-testid="admin-login-email"
                />
              </div>

              <div>
                <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>
                  Password
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showPw ? "text" : "password"} value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="rounded-xl h-11 pr-10"
                    style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }}
                    placeholder="••••••••"
                    required autoComplete="current-password"
                    data-testid="admin-login-password"
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: "#94a3b8" }}>
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="rounded-xl px-4 py-3 text-[13px] font-medium"
                  style={{ background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5" }}
                  data-testid="admin-login-error">
                  {error}
                </div>
              )}

              <button type="submit" disabled={loading}
                className="w-full h-12 rounded-xl text-white font-heading font-bold uppercase tracking-wide text-[15px] transition-all disabled:opacity-60 mt-2"
                style={{ background: loading ? "#94a3b8" : "#e31e24" }}
                data-testid="admin-login-submit">
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>

            <div className="mt-5 pt-5 flex items-center justify-center gap-2"
              style={{ borderTop: "1px solid #f1f5f9" }}>
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              <span className="text-[11px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>
                Pinnacle sync active
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}